import React, { useEffect, useState, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import moment from 'moment';
import { COUNTRY_CODES, CAMPAING_PROMISE_TYPES } from '../../../constants/enums';
import QueryStringService from '../../../services/queryStringService.js';
import styles from './CampaignDaysPlay.module.css';

const getCssVar = (name, fallback) => {
    if (typeof window === 'undefined' || !window.getComputedStyle) {
        return fallback;
    }

    const value = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();

    return value || fallback;
};

const CampaignDaysPlay = ({ orderlinesData, campaignPromiseType }) => {
    const [areaData, setAreaData] = useState([]);
    const [lineData, setLineData] = useState([]);
    const [lineName, setLineName] = useState('');
    const [areaName, setAreaName] = useState('');
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const queryStringService = useMemo(() => new QueryStringService(), []);

    const country = queryStringService.getCurrentCountry();

    useEffect(() => {
        switch (campaignPromiseType) {
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE:
                setLineName('Plays');
                setAreaName('EA VAC');
                break;
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER:
                setLineName('Plays');
                setAreaName('VAC');
                break;
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0:      
                setLineName('Plays');
                setAreaName('VAC/VI');
                break;                
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1:
                setLineName('Plays');
                setAreaName('Panels');
                break;
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0:
                setAreaName('VAC');
                break;
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0:                
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC:
                setLineName('Plays');
                break;
            default:
                setLineName('');
                setAreaName('');
        }
    }, [campaignPromiseType]);

    const getLineValue = (item) => {
        switch (campaignPromiseType) {
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV:
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:                
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0:
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1:
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS_2_0:                
                return item.delivered;

        }
    };

    const getAreaValue = (item) => {
        switch (campaignPromiseType) {
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC_FILLER: //EA-VAC
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_PLAYS: //EA-VAC
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC: //EA-VAC
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_EAVAC: //EA-VAC
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_SOV: //EA-VAC
            case CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE: //EA-VAC
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC: //EA-VAC
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER: //EA-VAC
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_2_0: //EA-VAC
            case CAMPAING_PROMISE_TYPES.NO_PLAY_VAC_FILLER_2_0:  //EA-VAC
            case CAMPAING_PROMISE_TYPES.NO_PLAY_SOV_2_0: //EA-VAC          
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS: //VAC
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC: //VAC
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS:
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_PANELS_2_0: //VAC
            case CAMPAING_PROMISE_TYPES.DK_PLAY_VAC_2_0: //VAC
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_2_0:
            case CAMPAING_PROMISE_TYPES.DK_AIRPORT_VAC_PANELS_2_0:                
                return item.delivered;
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS: //Panels
            case CAMPAING_PROMISE_TYPES.NO_PLAY_PLAYS_2_0: //Panels
            case CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1: //Panels
                return item.panels;
        }
    };

    useEffect(() => {
        if (orderlinesData &&
            orderlinesData.playData &&
            orderlinesData.playData.plays &&
            orderlinesData.playData.plays.campaignDays) {
            setLineData(orderlinesData.playData.plays.campaignDays);
        }
        if (orderlinesData &&
            orderlinesData.playData &&
            orderlinesData.playData.spotlengths &&
            orderlinesData.playData.useAlternateContacts &&
            orderlinesData.playData.spotlengths.length === 1 &&
            orderlinesData.playData.spotlengths[0] === 10 &&
            country !== COUNTRY_CODES.NORWAY
        ) {
            setAreaData(orderlinesData.playData.alternateContacts.campaignDays);
        } else if (orderlinesData && orderlinesData.playData && orderlinesData.playData.contacts) {
            setAreaData(orderlinesData.playData.contacts.campaignDays);
        }
    }, [orderlinesData]);

    useEffect(() => {
        if (areaData.length || lineData.length) {
            renderChart(); 
        }
    }, [areaData, lineData]);

    const renderChart = () => {
        const bmoMint = getCssVar('--bmo-primary-color-mint', '#00c6d7');

        chartRef.current = Highcharts.chart(containerRef.current, {
            chart: {
                type: 'area',
            //    height: 250
            },
            credits: {
                enabled: false
            },
            title: {
                text: null,
            },
            tooltip: {
                formatter: function () {
                    const header = `${this.key}<br/>`; // Header for category or series
                    const body = `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${this.y
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</b>`;
                    return header + body;
                },
                style: {
                    fontSize: '12px',
                    fontFamily: "'BauerMediaSans', sans-serif"
                }
            },
            xAxis: {
                categories: areaData.map((x) => moment(x.date).format('YYYY-MM-DD HH:mm')),
                tickLength: 5,
                tickWidth: 0.3,
                lineWidth: 0.3,
                labels: {
                    formatter: function () {
                        var label = this.axis.defaultLabelFormatter.call(this);
                        return moment(label).format('MMM-DD');
                    },
                    style: {
                        fontFamily: "'BauerMediaSans', sans-serif"
                    },
                },
            },
            legend: {
                enabled: false,
            },
            yAxis: [
                {
                    title: {
                        text: areaName,
                        style: {
                            fontSize: '11px',
                            fontFamily: "'BauerMediaSans', sans-serif"
                        },
                    },
                    labels: {
                        enabled: areaName !== '',
                        formatter: function () {
                            return this.value
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                        },
                        style: {
                            fontSize: '11px',
                            fontFamily: "'BauerMediaSans', sans-serif"
                        },
                    },
                    min: areaName !== '' ? Math.min(...areaData.map((x) => getAreaValue(x))) * 0.75 : 0,
                    max: areaName !== '' ? Math.max(...areaData.map((x) => getAreaValue(x))) * 1.1 : 0,
                },
                {
                    title: {
                        text: lineData.length > 0 ? lineName : '',
                        style: {
                            fontSize: '11px',
                            fontFamily: "'BauerMediaSans', sans-serif"
                        },
                    },
                    labels: {
                        enabled: true,
                        formatter: function () {
                            return this.value
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                        },
                        style: {
                            fontSize: '11px',
                            fontFamily: "'BauerMediaSans', sans-serif"
                        },
                    },
                    min: Math.min(...lineData.map((x) => getLineValue(x))) * 0.75,
                    max: Math.max(...lineData.map((x) => getLineValue(x))) * 1.9,
                    opposite: areaName !== '',
                },
            ],
            series: getSeries(bmoMint),
        });
    };

    const getSeries = (bmoMint) => {
        let series = [
            
        ];
        if (campaignPromiseType !== CAMPAING_PROMISE_TYPES.DK_PLAY_PLAYS && campaignPromiseType !== CAMPAING_PROMISE_TYPES.SE_DIGITAL_VAC) {
            series.push({
                yAxis: 0,
                name: areaName,
                color: 'black',
                type: 'area',
                data: areaData.map((x) => getAreaValue(x)),
            });
        }
        if (lineData.length > 0) {
            series.push({
                yAxis: 1,
                name: lineName,
                color: bmoMint,
                type: 'line',
                marker: {
                    enabled: false,
                },
                data: lineData.map((x) => getLineValue(x)),
            });
        }
        return series;
    };

    return (
        <div className={styles.chartShadow}>
            <div className={styles.graphLabel}>
                <h4>Campaign period</h4>
            </div>
            <div>
                <div className={styles.dayGraph} ref={containerRef} id="pod_campaign_days_container"></div>
            </div>
        </div>
    );
};

export default CampaignDaysPlay;
